/**
 * GuideAgent — D-5 (CRM UI streamline Phase 3, option A): the old SteerBar's
 * standing 2x4 grid collapsed to ONE "Guide agent" button that opens a popover
 * holding the same seven steers, the same free text, and the same per-click
 * timing choice. AgentPanel mounts it; this file is standalone and never
 * imports or touches AgentPanel.
 *
 * Semantics are untouched, not reimplemented: STEERS (the owner's fixed seven,
 * in the owner's order) is imported from `steers` — the single source of
 * truth — and the click/send flow below is the retired bar's, transplanted
 * verbatim (same endpoints, same request bodies, same setState reconciliation
 * off the POST response, the same staged-toggle-off on re-click, the same flash
 * strings). A steer here still never pauses or hands off the conversation.
 *
 * Popover behavior, per the D-2 overlay build's house rule: the panel stays
 * MOUNTED at all times and is only ever toggled with the `hidden` class, never
 * conditionally unmounted — so a half-typed "Tell the agent…" note or a staged
 * steer survives closing and reopening the popover, exactly like it survives a
 * card getting bumped in the D-2 stack. Escape is bound on `window` in the
 * BUBBLE phase, added only while open: the kit's ConfirmDialog owns Escape in
 * the CAPTURE phase (and stops it) so a confirm raised from inside this
 * popover closes itself first, never this popover and the confirm in the same
 * keystroke. Outside clicks close via a `document` mousedown listener gated by
 * ref containment, so a click anywhere in the trigger+panel wrapper (a steer
 * button, the note textarea, the timing buttons) never counts as "outside".
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Send, Clock, X, Wand2 } from 'lucide-react';
import api from '../../../services/api';
import { fieldCls } from '../filters';
import { Z } from '../ui';
import { btnGhost } from './shared';
import { STEERS, type SteerDef, type SteerKey } from './steers';
import AgentDecision, { type DecisionKey } from './AgentDecision';

/** Mirrors SteerBar's private state shape — same GET response, unchanged. */
interface SteerState {
  steer: { key: string; note?: string | null; timing?: string; at?: string } | null;
  prefs: Record<string, boolean>;
  steerable: boolean;
  // Q&A + call-only studies (2026-08-13): screening-shaped steers the backend
  // will refuse — hidden here so nobody clicks into a 409. Absent on older
  // responses ⇒ nothing hidden.
  blocked_steers?: string[];
}

export default function GuideAgent({
  conversationId, onError, onChanged,
}: {
  conversationId: string;
  onError: (msg: string) => void;
  onChanged?: () => void;
}) {
  const [state, setState] = useState<SteerState | null>(null);
  const [staged, setStaged] = useState<SteerDef | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState<SteerKey | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  // The eligibility decision's open form. Held HERE rather than inside
  // AgentDecision so the popover can only ever have one form open: staging a
  // steer clears it, and picking a decision clears the staged steer.
  const [decision, setDecision] = useState<DecisionKey | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const load = useCallback(() => {
    api.get(`/api/crm/agent/conversations/${conversationId}/steer`)
      .then((res) => setState(res.data))
      .catch(() => { /* the popover just stays quiet — never blocks the panel */ });
  }, [conversationId]);

  useEffect(() => { load(); }, [load]);

  // Escape (bubble) + outside click — only while the popover is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  // --- SteerBar's send/click, verbatim ---------------------------------
  const send = async (def: SteerDef, timing: 'next_reply' | 'now') => {
    setBusy(def.key);
    try {
      const res = await api.post(`/api/crm/agent/conversations/${conversationId}/steer`, {
        key: def.key,
        timing,
        note: def.requiresNote ? note.trim() : undefined,
      });
      setState((prev) => (prev ? { ...prev, steer: res.data.steer?.kind === 'next_turn' ? res.data.steer : prev.steer, prefs: res.data.prefs || prev.prefs } : prev));
      setStaged(null);
      setNote('');
      setFlash(def.shape === 'toggle'
        ? `${def.label} ${res.data.steer?.on ? 'on' : 'off'}`
        : timing === 'now' ? `${def.label} — the agent is composing it now` : `${def.label} — on their next reply`);
      onChanged?.();
      load();
    } catch (err: any) {
      // A refusal that explains itself (a steer on a scheduling-assistant
      // conversation) shows its sentence; every other failure reads as before.
      const refusal = err?.response?.status === 409 && typeof err?.response?.data?.detail === 'string'
        ? err.response.data.detail : null;
      onError(`Steer failed: ${refusal || err?.response?.data?.error || err?.message || 'request failed'}`);
    } finally {
      setBusy(null);
    }
  };

  const click = (def: SteerDef) => {
    setDecision(null);
    if (def.shape === 'toggle' || def.shape === 'once') { send(def, 'next_reply'); return; }
    setStaged((prev) => (prev?.key === def.key ? null : def));
    setNote('');
  };
  // -----------------------------------------------------------------------

  if (!state) return null;
  const disabled = !state.steerable;
  const blocked = new Set(state.blocked_steers || []);
  const visibleSteers = STEERS.filter((s) => !blocked.has(s.key));
  const activeToggles = visibleSteers.filter((s) => s.shape === 'toggle' && state.prefs?.[s.key] === true);

  return (
    <div className="mt-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
          Suggested next steps
        </div>
        {state.steer && (
          <div className="text-[11px] text-primary-600 dark:text-primary-400">
            Pending: {STEERS.find((s) => s.key === state.steer?.key)?.label || state.steer.key}
          </div>
        )}
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
        <div ref={wrapRef} className="relative inline-block">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-haspopup="dialog"
            aria-expanded={open}
            className={btnGhost}
          >
            <Wand2 className="h-4 w-4" />
            Guide agent
          </button>

          {/* Stays mounted always — `hidden` toggles visibility, never unmount
              (D-2 house rule; see top-of-file comment). The phone-only cap is
              the cost of the one-column list below: seven rows carrying their
              own explainer are taller than a phone, and this panel is anchored
              `top-full` with nothing above it to scroll. */}
          <div
            role="dialog"
            aria-label="Guide the agent"
            className={`absolute left-0 top-full ${Z.overlay} mt-1.5 w-80 max-w-[min(20rem,calc(100vw-2.5rem))] rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-600 dark:bg-gray-900 max-sm:max-h-[65vh] max-sm:overflow-y-auto max-sm:overscroll-contain ${open ? '' : 'hidden'}`}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Guide the agent</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close Guide agent"
                className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-1.5 text-gray-400 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:min-h-0 sm:min-w-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* One column on a phone: each steer is a full-width row whose
                explainer is part of the row (see below), which a 2-up grid has
                no width for. `sm` up keeps the owner's 2x4. */}
            <div className="mt-2 grid grid-cols-2 gap-1.5 max-sm:grid-cols-1">
              {visibleSteers.map((s) => {
                const lit = s.shape === 'toggle' ? state.prefs?.[s.key] === true : staged?.key === s.key;
                return (
                  <div key={s.key} className="group relative">
                    <button
                      type="button"
                      disabled={disabled || busy !== null}
                      onClick={() => click(s)}
                      aria-pressed={lit}
                      className={`w-full rounded-lg border px-2 py-1.5 text-center text-xs font-medium transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 max-sm:text-left ${lit
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:border-primary-500 dark:bg-primary-900/20 dark:text-primary-300'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700'}`}
                    >
                      {busy === s.key ? <Loader2 className="mx-auto h-3.5 w-3.5 animate-spin" /> : s.label}
                      {/* A phone has no hover, and a toggle/'once' steer fires on
                          the first tap — so the tooltip below was an explainer
                          nobody on a phone could ever read before committing.
                          Under `sm` the same `s.tip` rides INSIDE the button, so
                          the row stays one tap target. (`line-clamp-2` is used
                          WITHOUT `block`: Tailwind emits `.block` after
                          `.line-clamp-2` and would undo the clamp.) */}
                      <span className="mt-0.5 line-clamp-2 text-[11px] font-normal leading-snug text-gray-500 dark:text-gray-400 sm:hidden">
                        {s.tip}
                      </span>
                      {s.shape === 'toggle' && lit && (
                        <span className="mt-0.5 block text-[11px] font-normal leading-snug text-gray-500 dark:text-gray-400 sm:hidden">
                          On — tap again to turn off.
                        </span>
                      )}
                    </button>
                    {/* Unchanged from `sm` up. Its two triggers are `sm:`-scoped
                        because under `sm` the tip is already in the button —
                        tapping a steer focuses it, and focus-within would pop a
                        duplicate of text the row is already showing. */}
                    <div
                      role="tooltip"
                      className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-1.5 hidden w-60 -translate-x-1/2 rounded-lg border border-gray-200 bg-white p-2 text-left text-[11px] leading-relaxed text-gray-600 shadow-lg sm:group-hover:block sm:group-focus-within:block dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300"
                    >
                      <span className="font-semibold text-gray-900 dark:text-gray-100">{s.label}.</span> {s.tip}
                      {s.shape === 'toggle' && <span className="mt-1 block italic">Sticky — click again to turn off.</span>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Timing picker — same markup as SteerBar's. */}
            {staged && (
              <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 dark:border-gray-600 dark:bg-gray-800/50">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[11px] leading-relaxed text-gray-600 dark:text-gray-300">{staged.tip}</p>
                  <button
                    type="button"
                    onClick={() => { setStaged(null); setNote(''); }}
                    aria-label="Cancel this steer"
                    className="rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
                {staged.requiresNote && (
                  <textarea
                    value={note}
                    onChange={(ev) => setNote(ev.target.value)}
                    rows={2}
                    maxLength={600}
                    autoFocus
                    placeholder="What should the agent do next? e.g. “Mention we have evening appointments — she said mornings are hard.”"
                    className={`${fieldCls} mt-2 w-full resize-y`}
                  />
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    disabled={busy !== null || (staged.requiresNote && !note.trim())}
                    onClick={() => send(staged, 'next_reply')}
                    className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 text-xs font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Clock className="h-3.5 w-3.5" />
                    On their next reply
                  </button>
                  <button
                    type="button"
                    disabled={busy !== null || (staged.requiresNote && !note.trim())}
                    onClick={() => send(staged, 'now')}
                    className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
                  >
                    <Send className="h-3.5 w-3.5" />
                    Send now
                  </button>
                  <span className="text-[11px] text-gray-400 dark:text-gray-500">
                    Either way the agent keeps going — nothing is paused or handed off.
                  </span>
                </div>
              </div>
            )}

            {/* Owner ask 2026-08-06: the eligibility call, in the same place a
                recruiter is already guiding the agent from. BELOW the steers on
                purpose — the light touch is what you should reach for first,
                and this one auto-sends and ends screening. */}
            <AgentDecision
              conversationId={conversationId}
              disabled={disabled}
              selected={decision}
              onSelect={(k) => { setDecision(k); if (k) { setStaged(null); setNote(''); } }}
              onError={onError}
              onDone={(msg) => {
                setStaged(null);
                setNote('');
                setFlash(msg);
                // The determination is the end of guiding this thread — close
                // the popover so the panel's own strip is what you look at next.
                setOpen(false);
                onChanged?.();
                load();
              }}
            />

            {disabled && (
              <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                This conversation is closed — steering applies to live threads only.
              </p>
            )}

            {flash && !staged && (
              <p className="mt-1.5 text-[11px] text-green-700 dark:text-green-400">{flash}</p>
            )}
          </div>
        </div>

        {activeToggles.map((s) => (
          <span
            key={s.key}
            title={`${s.label} is on. Open Guide agent to turn it off.`}
            className="rounded-full bg-primary-100 px-2 py-0.5 text-[11px] font-medium text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"
          >
            {s.label} · on
          </span>
        ))}
      </div>

      {/* Same flash line, same slot, only when the popover itself isn't
          already showing it (see the copy inside the panel above). */}
      {flash && !staged && !open && (
        <p className="mt-1.5 text-[11px] text-green-700 dark:text-green-400">{flash}</p>
      )}
    </div>
  );
}
